import { cn } from "@plasma/utils/cn";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import type * as React from "react";

import { Dialog, DialogContent } from "~/components/ui/dialog";

function Command({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      className={cn(
        "bg-background text-foreground dark:bg-background dark:text-foreground flex h-full w-full flex-col overflow-hidden rounded-md",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

interface CommandDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

function CommandDialog({ children, ...props }: CommandDialogProps) {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:text-weight-medium dark:[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-9 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div className="flex items-center border-b px-4 py-1" cmdk-input-wrapper="">
      <Search className="text-muted-foreground mr-2 size-5 shrink-0" />
      <CommandPrimitive.Input
        className={cn(
          "placeholder:text-muted-foreground dark:placeholder:text-muted-foreground flex h-9 w-full rounded-md bg-transparent focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      className={cn(
        "scrollbar-track-background scrollbar-thumb-muted scrollbar-thumb-rounded-full max-h-64 scrollbar-thin overflow-x-hidden overflow-y-auto",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function CommandEmpty({
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      className="body-sm py-6 text-center"
      ref={ref}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      className={cn(
        "[&_[cmdk-group-heading]]:subtitle-sm text-foreground dark:text-foreground [&_[cmdk-group-heading]]:bg-muted [&_[cmdk-group-heading]]:text-foreground p-2 [&_[cmdk-group-heading]]:rounded-xl [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      className={cn("bg-border dark:bg-border -mx-1 h-px", className)}
      ref={ref}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      className={cn(
        "text-foreground aria-selected:bg-accent aria-selected:text-accent-foreground dark:aria-selected:bg-accent dark:aria-selected:text-accent-foreground relative flex cursor-pointer items-center rounded-sm px-2 py-1.5 outline-hidden transition-[color,box-shadow] select-none hover:font-normal data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        "caption text-muted-foreground dark:text-muted-foreground ml-auto tracking-widest",
        className
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
